import React, { useState, useEffect } from "react"; 
import axios from "axios";
import ReactPixel from "react-facebook-pixel";

const LeadGenForm = ({ onSuccess, onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [ipInfo, setIpInfo] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketingParams, setMarketingParams] = useState({});
  const [serverError, setServerError] = useState("");
  const subDomain = window.location.hostname.split('.')[0];

  // Load marketing params from localStorage
  useEffect(() => {
    const storedParams = localStorage.getItem("marketingParams");
    if (storedParams) {
      setMarketingParams(JSON.parse(storedParams));
    }
  }, []);

  // Fetch IP info
  useEffect(() => {
    const fetchIpInfo = async () => {
      try {
        const token = "ad22ffe4eb7ee0"; // replace with your token if needed
        const response = await axios.get(`https://ipinfo.io/json?token=${token}`);
        setIpInfo(response.data);
      } catch (error) {
        console.error("Error fetching IP info:", error);
      }
    };
    fetchIpInfo();
  }, []);

  // Phone formatting logic
  const formatPhoneNumber = (input) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, "");
    if (cleaned.length <= 3) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, ""); // Remove non-numeric characters
    return cleaned.length === 10;
  };

  const validate = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    } else if (!/^[a-zA-Z]{2,}$/.test(firstName.trim())) {
      newErrors.firstName = "Enter a valid first name.";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    } else if (!/^[a-zA-Z]{2,}$/.test(lastName.trim())) {
      newErrors.lastName = "Enter a valid last name.";
    }

    if (companyName && companyName.trim().length < 2) {
      newErrors.companyName = "Enter a valid company name.";
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone =
        "Please enter a valid phone number in the format: (123) 456-7890";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim())
    ) {
      newErrors.email = "Enter a valid email address.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    ReactPixel.track("Lead");
    setIsSubmitting(true);
    setServerError("");

    try {
      const payload = {
        firstName,
        lastName,
        companyName,
        phone,
        email,
        ip: ipInfo.ip || "",
        location: ipInfo.loc || "",
        city: ipInfo.city || "",
        region: ipInfo.region || "",
        country: ipInfo.country || "",
        marketingParams,
        subDomain,
      };

      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/dataServer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "captureLead",
          payload,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        setServerError(data.message);
      } else {       
        onSuccess && onSuccess();
      }
    } catch (error) {
      setServerError(error.message);
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg w-full mx-auto relative z-50 p-6 sm:px-6 px-4 my-4 bg-white shadow-lg rounded animate-fadeIn transition-all duration-300">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        aria-label="Close"
      >
        &#x2715;
      </button>
      <h2 className="text-2xl font-bold mb-4 text-center">Nice to meet you!</h2>
      <p className="text-gray-600 mb-6 text-center">
        We're happy you're here and excited to show you how we can help you save at the pump.
        Please let us know who you are so we can get started.
      </p>
      {serverError && (
        <div className="text-red-500 text-sm text-center mb-4">
          {serverError}
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <label className="block text-gray-700">First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring"
            required
          />
          {errors.firstName && (
            <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
          )}
        </div>
        <div>
          <label className="block text-gray-700">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring"
            required
          />
          {errors.lastName && (
            <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
          )}
        </div>
        <div>
          <label className="block text-gray-700">
            Company Name <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring"
          />
          {errors.companyName && (
            <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
          )}
        </div>
        <div>
          <label className="block text-gray-700">Phone</label>
          <input
            type="text"
            value={phone}
            onChange={handlePhoneChange}
            className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring"
            required
            placeholder="(123) 456-7890"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
          )}
        </div>
        <div>
          <label className="block text-gray-700">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring"
            required
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full themed-button transition duration-200"
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default LeadGenForm;
